class SCLocalStorage {
  constructor() {
    this.lsKeys = {
      currentUser: 'log_in_data',
      authToken: 'sc-auth-token',
      unreadNotifications: 'sc-u-notifs',
      isSendJobCreateMetrics: 'is_send_job_create_metrics',
      lastSessionReportDate: 'last_session_report_date',
      referralCode: 'referral_code',
      offerId: 'swipecast_offer_id',
      jobsListRoleSwitcher: 'jobs_list_role_switcher',
      specialBookingOnboardingRead: 'special-booking-onboarding-read',
      utm: 'utm_data',
      pushPermissionsAskedAt: 'push-permission-asked-at',
      pushPermissionsRemindAt: 'push-permission-remind-at',
      sessionId: 'session_id',
      referrer: 'referrer',
      talentApplyCount: 'talent_apply_count',
      referralAfterApplyDismissedAt: 'referral_after_apply_modal_dismissed_at',
      chatReferralBannerDismissedAt: 'chat_referral_banner_dismissed_at',
    }
  }

  // Fetch a record from Local Storage and optionally return it's parsed JSON
  fetch = (key, json = true) => {
    const record = localStorage.getItem(key)
    if (!record) return ''

    try {
      if (json) return JSON.parse(record)
    } catch {
      return record
    }

    return record
  }

  set = (key, value, json = false) => {
    let v = value
    if (json) {
      v = JSON.stringify(value)
    }

    localStorage.setItem(key, v)
  }

  remove = (key) => {
    localStorage.removeItem(key)
  }

  fetchUTM = () => {
    const result = this.fetch(this.lsKeys.utm, true)
    if (result === '') return ''

    if (result.expiration && new Date(result.expiration) < new Date()) {
      this.remove(this.lsKeys.utm)
      return ''
    }

    return result
  }

  fetchPushPermissionsRemindAt = () => {
    const remindAt = this.fetch(this.lsKeys.pushPermissionsRemindAt)
    return remindAt ? new Date(remindAt) : null
  }
  fetchPushPermissionsAskedAt = () => this.fetch(this.lsKeys.pushPermissionsAskedAt)
  fetchSessionId = () => this.fetch(this.lsKeys.sessionId)
  fetchCurrentUser = () => this.fetch(this.lsKeys.currentUser)
  fetchReferrer = () => this.fetch(this.lsKeys.referrer)
  fetchReferralCode = () => this.fetch(this.lsKeys.referralCode)
  fetchOfferID = () => this.fetch(this.lsKeys.offerId)
  fetchOfferOnboardingViewed = () => this.fetch(this.lsKeys.specialBookingOnboardingRead)
  fetchAuthToken = () => this.fetch(this.lsKeys.authToken, false)
  // fetchUnreadNotifications = () => this.fetch(this.lsKeys.unreadNotifications);

  setUTM = (value) => {
    const EXPIRATION_DAYS = 7
    const expiration = new Date().setDate(new Date().getDate() + EXPIRATION_DAYS)
    this.set(this.lsKeys.utm, { ...value, expiration }, true)
  }
  setReferrer = () => {
    const referrer = document.referrer?.trim()
    // we should only set referrer if it is present and not just whitespace
    if (referrer) this.set(this.lsKeys.referrer, referrer)
  }

  setPushPermissionsAskedAt = () => this.set(this.lsKeys.pushPermissionsAskedAt, new Date())
  setPushPermissionsRemindAt = (date) => this.set(this.lsKeys.pushPermissionsRemindAt, date)
  setCurrentUser = (value) => this.set(this.lsKeys.currentUser, value)
  setAuthToken = (value) => this.set(this.lsKeys.authToken, value)
  setReferralCode = (v) => this.set(this.lsKeys.referralCode, v)
  setOfferID = (v) => this.set(this.lsKeys.offerId, v)
  setOfferOnboardingViewed = (v) => this.set(this.lsKeys.specialBookingOnboardingRead, v)
  // setUnreadNotifications = value =>
  //   this.set(this.lsKeys.unreadNotifications, value);

  // project metrics
  fetchProjectCreateMetricsSend = () => {
    return this.fetch(this.lsKeys.isSendJobCreateMetrics, true)
  }
  getProjectCreateMetricsSend = (step) => {
    let metricIndex = step
    if (typeof step === 'number') {
      metricIndex = metricIndex.toString()
    }
    return this.fetchProjectCreateMetricsSend()[metricIndex]
  }

  // Talent apply count
  fetchTalentApplyCount = () => {
    const count = this.fetch(this.lsKeys.talentApplyCount, false)
    return count ? parseInt(count, 10) : 0
  }
  incrementTalentApplyCount = () => {
    const newCount = this.fetchTalentApplyCount() + 1
    this.set(this.lsKeys.talentApplyCount, newCount.toString())
    return newCount
  }
  decrementTalentApplyCount = () => {
    const newCount = Math.max(0, this.fetchTalentApplyCount() - 1)
    this.set(this.lsKeys.talentApplyCount, newCount.toString())
    return newCount
  }

  // Referral after apply modal
  fetchReferralAfterApplyDismissedAt = () => {
    const timestamp = this.fetch(this.lsKeys.referralAfterApplyDismissedAt, false)
    return timestamp ? parseInt(timestamp, 10) : null
  }
  setReferralAfterApplyDismissedAt = () => {
    this.set(this.lsKeys.referralAfterApplyDismissedAt, Date.now().toString())
  }

  // Chat referral banner
  fetchChatReferralBannerDismissedAt = () => {
    const timestamp = this.fetch(this.lsKeys.chatReferralBannerDismissedAt, false)
    if (!timestamp) return null
    const parsed = parseInt(timestamp, 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  setChatReferralBannerDismissedAt = () => {
    this.set(this.lsKeys.chatReferralBannerDismissedAt, Date.now().toString())
  }
  shouldShowChatReferralBanner = () => {
    const dismissedAt = this.fetchChatReferralBannerDismissedAt()
    if (!dismissedAt || !Number.isFinite(dismissedAt)) return true

    const dismissedDate = new Date(dismissedAt)
    const now = new Date()
    const daysDiff = (now - dismissedDate) / (1000 * 60 * 60 * 24)
    
    return daysDiff >= 45
  }

  removePushPermissionsRemindAt = () => this.remove(this.lsKeys.pushPermissionsRemindAt)
  removeOfferID = () => this.remove(this.lsKeys.offerId)
  removeReferralCode = () => this.remove(this.lsKeys.referralCode)
  removeProjectCreateMetricsSend = () => this.remove(this.lsKeys.isSendJobCreateMetrics) // called on new job creation init (first step mount)
  setProjectCreateMetricsSend = (step) => {
    // call on each step submition
    const prevMetrics = this.fetchProjectCreateMetricsSend()
    const newMetrics = { ...prevMetrics, [step]: true }
    this.set(this.lsKeys.isSendJobCreateMetrics, newMetrics, true)
  }
}

export const LocalStorage = new SCLocalStorage()
export default SCLocalStorage
